import { AddIcon, EditIcon, EmptyState } from '@bp/ui-components';
import classNames from 'classnames';
import { graphql } from 'client/gql';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { AssignmentModal } from 'components/Assignments/modals/AssignmentModal';
import { useRequireParams } from 'hooks/useRequireParams';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { AppointmentsPreviewTable } from '../AppointmentsPreviewTable/AppointmentsPreviewTable';
import { AssignmentsPreviewTable } from '../Assignments/tables/AssignmentsPreviewTable';
import { BpCard } from '../BpCard/BpCard';
import { BpTipTapText } from '../BpTipTapText/BpTipTapText';
import { FilePreviewTable } from '../FilePreviewTable/FilePreviewTable';
import { WorkMaterialsPreviewTable } from '../WorkMaterialsPreviewTable/WorkMaterialsPreviewTable';
import styles from './TeachingUnit.module.scss';

const DeleteCalendarEventMutationDocument = graphql(`
  mutation BpDeleteCalendarEventFromTeachingUnit($where: CalendarEventWhere!) {
    deleteCalendarEvents(where: $where) {
      nodesDeleted
    }
  }
`);

const DeleteWorkMaterialsMutationDocument = graphql(`
  mutation BpDeleteWorkMaterialsFromTeachingUnit($where: WorkMaterialWhere!) {
    deleteWorkMaterials(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

const TeachingUnitPageQueryDocument = graphql(`
  query TeachingUnitPageData($teachingUnitUuid: ID!) {
    calendarEvents(where: { teachingUnit: { uuid: $teachingUnitUuid } }) {
      uuid
      __typename
      title
      start
    }
    workMaterials(where: { holder: { TeachingUnit: { uuid: $teachingUnitUuid } } }) {
      uuid
      __typename
      title
      visibleFrom {
        uuid
        __typename
        date
      }
      visibleTo {
        uuid
        __typename
        date
      }
      publicFrom {
        uuid
        __typename
        date
      }
      visibleAfterVisibleTo
    }
    teachingUnits(where: { uuid: $teachingUnitUuid }) {
      uuid
      __typename
      title
      note {
        uuid
        __typename
        text
        fileEntries {
          uuid
          __typename
          ...FilePreviewTableFields
        }
      }
      description
    }
  }
`);

export type TeachingUnitProps = {
  teachingUnitUuid: string;
  onTeachingUnitEdit: (teachingUnitUuid: string) => void;
  onAssignmentNavigate: (assigmentUuid: string) => void;
  onNewAppointment: (teachingUnitUuid: string) => void;
  onNewWorkMaterial: (teachingUnitUuid: string) => void;
  onNoteEdit: (teachingUnitUuid: string) => void;
  onEventEdit: (eventUuid: string) => void;
  onWorkMaterialEdit: (workmaterialUuid: string) => void;
  onAppointmentNavigate: (appointmentUuid: string) => void;
  onWorkMaterialNavigate: (workMaterialUuid: string) => void;
  className?: string | undefined;
};

export const TeachingUnit: FC<TeachingUnitProps> = ({
  teachingUnitUuid,
  onTeachingUnitEdit,
  onAssignmentNavigate,
  onNewWorkMaterial,
  onNewAppointment,
  onEventEdit,
  onNoteEdit,
  onWorkMaterialEdit,
  onAppointmentNavigate,
  onWorkMaterialNavigate,
  className,
}) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const [{ data }] = useQuery({
    query: TeachingUnitPageQueryDocument,
    variables: { teachingUnitUuid },
  });

  const {
    assignments,
    modalState: assignmentModalState,
    ConfirmationDialog: AssignmentDeleteConfirmationDialog,
    loading: assignmentLoading,
    handleCreate: handleAssginmentCreate,
    handleEdit: handleAssignmentEdit,
    handleDelete: handleAssignmentDelete,
  } = useAssignments({ courseUuid, teachingUnitUuid });

  const events = data?.calendarEvents;
  const lesson = data?.teachingUnits[0];
  const workMaterials = data?.workMaterials;

  const [, deleteWorkMaterial] = useMutation(DeleteWorkMaterialsMutationDocument);
  const [, deleteEvent] = useMutation(DeleteCalendarEventMutationDocument);

  const classes = classNames(styles['teaching-unit'], className);

  return (
    <div className={classes}>
      {!lesson ? (
        <EmptyState
          forcedHeight='45vh'
          fitParent
          title={t('teachingUnits.noTeachingUnits')}
          subtitle={t('teachingUnits.noTeachingUnitsHint')}
          iconColor='var(--color-primary-light)'
        />
      ) : (
        <div className={styles.wrapper}>
          <BpCard
            preventCollapsible
            className={styles.unit}
            hideContent={!lesson.description}
            header={{
              headline: lesson.title,
              actions: [
                {
                  text: t('common.edit'),
                  callback: () => onTeachingUnitEdit(teachingUnitUuid),
                  icon: <EditIcon data-testid={'editTu'} className='svg-icon small' />,
                },
              ],
            }}
          >
            <BpTipTapText embedded content={lesson.description ?? ''} />
          </BpCard>
          <div className={classNames(styles.columns, styles.top)}>
            <BpCard
              noPadding
              noMargin
              header={{
                headline: t('assignments.title', { count: 2 }),
                subHeadline: assignments.length,
                actions: [
                  {
                    text: t('common.add'),
                    callback: () => handleAssginmentCreate(),
                    icon: <AddIcon className='small' />,
                  },
                ],
              }}
            >
              <AssignmentsPreviewTable
                teachingUnitUuid={teachingUnitUuid}
                loading={!!assignmentLoading}
                onNavigate={(uuid) => onAssignmentNavigate(uuid)}
                onEdit={(uuid) => handleAssignmentEdit(uuid)}
                onDelete={(uuid) => handleAssignmentDelete(uuid)}
                isGroupEditor={true}
              />
            </BpCard>
            <BpCard
              noPadding
              noMargin
              header={{
                headline: t('appointments.title'),
                subHeadline: events?.length,
                actions: [
                  {
                    text: t('common.add'),
                    callback: () => onNewAppointment(teachingUnitUuid),
                    icon: <AddIcon className='svg-icon small' />,
                  },
                ],
              }}
            >
              <AppointmentsPreviewTable
                data={
                  events?.map((event) => {
                    return {
                      uuid: event.uuid,
                      name: event.title ?? '',
                      date: event.start,
                    };
                  }) ?? []
                }
                onNavigate={(uuid) => onAppointmentNavigate(uuid)}
                onEdit={(uuid) => onEventEdit(uuid)}
                onDelete={(uuid) => deleteEvent({ where: { uuid: uuid } })}
                isGroupEditor={true}
              />
            </BpCard>
          </div>
          <div className={classNames(styles.columns, styles.bottom)}>
            <BpCard
              noPadding
              noMargin
              header={{
                headline: t('workmaterials.title'),
                subHeadline: workMaterials?.length,
                actions: [
                  {
                    text: t('common.add'),
                    callback: () => onNewWorkMaterial(teachingUnitUuid),
                    icon: <AddIcon className='svg-icon small' />,
                  },
                ],
              }}
            >
              <WorkMaterialsPreviewTable
                data={
                  workMaterials?.map((wm) => {
                    return {
                      uuid: wm.uuid,
                      name: wm.title,
                      relevantFrom: wm.visibleFrom?.date ?? '',
                      relevantTo: wm.visibleTo?.date ?? '',
                      visibleFrom: wm.publicFrom?.date ?? '',
                      visibleAfterVisibleTo: !!wm.visibleAfterVisibleTo,
                    };
                  }) ?? []
                }
                onNavigate={(uuid) => onWorkMaterialNavigate(uuid)}
                onEdit={(uuid) => onWorkMaterialEdit(uuid)}
                onDelete={(uuid) => deleteWorkMaterial({ where: { uuid: uuid } })}
              />
            </BpCard>
            <BpCard
              noPadding
              noMargin
              className={styles.notes}
              header={{
                headline: t('notes.title', { count: 1 }),
                actions: [
                  {
                    text: t('common.edit'),
                    callback: () => onNoteEdit(lesson?.note?.uuid ?? ''),
                    icon: <EditIcon className='svg-icon small' />,
                  },
                ],
              }}
            >
              {!lesson.note || !lesson.note.text || !lesson.note.fileEntries ? (
                <EmptyState size='small' hideIcon subtitle={t('notes.noNotes')} fitParent forcedHeight='150px' />
              ) : (
                <>
                  {lesson.note.text && (
                    <div className={classNames(styles.note, 'has-scrollbar')}>
                      <BpTipTapText content={lesson.note.text} customPadding='0 var(--spacing-6)' />
                    </div>
                  )}
                  {lesson.note.fileEntries && (
                    <FilePreviewTable
                      className={styles.files}
                      hasNote={!!lesson.note.text}
                      query={lesson.note.fileEntries}
                      isGroupEditor={true}
                    />
                  )}
                </>
              )}
            </BpCard>
          </div>

          <AssignmentModal {...assignmentModalState} teachingUnitUuid={teachingUnitUuid} />

          <AssignmentDeleteConfirmationDialog />
        </div>
      )}
    </div>
  );
};
